import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useHasAnyPermission, useHasPermission } from '@/hooks/permissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { TeamMemberCard } from '@/components/cards/TeamMemberCard';
import { ViewProfileDialog } from '@/components/profile';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Users, UserPlus, Mail, Shield, DollarSign, Edit, RotateCcw, Archive, UserCheck, Crown, MoreHorizontal, Eye, Loader2, Trash2, AlertTriangle, XCircle } from 'lucide-react';
import { generateInitials, generateDisplayName } from '@/utils/avatarUtils';

// React Query imports
import { useTeamMembers, useTeamMutations, type InviteUserInput } from '@/hooks/team';
import { useRoles } from '@/hooks/roles/useRoles';

const inviteFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  roleId: z.string().min(1, 'Please select a role'),
});

type InviteFormValues = z.infer<typeof inviteFormSchema>;

const TeamManagement = () => {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const isMobile = useIsMobile();
  
  // Permission-based access control (replaces legacy role check)
  const hasTeamAccess = useHasAnyPermission(['view_employee_details', 'manage_employee_details']);
  // Permission for managing employees (archive/reactivate)
  const canManageEmployees = useHasPermission('manage_employee_details');
  
  // React Query hooks
  const { data: teamMembers = [], isLoading: loading } = useTeamMembers();
  const { data: roles = [], isLoading: rolesLoading } = useRoles();
  const { 
    inviteUser, 
    archiveMember, 
    reactivateMember, 
    resendInvitation,
    deleteMember,
    cancelPendingInvitation
  } = useTeamMutations();
  
  // Get default role (Staff) for form initialization
  const defaultRoleId = roles.find(r => r.slug === 'staff')?.id || '';

  // Local state for UI interactions
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewProfileOpen, setViewProfileOpen] = useState(false);
  const [selectedMemberForView, setSelectedMemberForView] = useState<any>(null);
  
  // Track which specific member is targeted by each mutation
  const [pendingResendMemberId, setPendingResendMemberId] = useState<string | null>(null);
  const [archivingMemberId, setArchivingMemberId] = useState<string | null>(null);
  const [reactivatingMemberId, setReactivatingMemberId] = useState<string | null>(null);
  const [deletingArchivedMemberId, setDeletingArchivedMemberId] = useState<string | null>(null);
  const [cancellingPendingMemberId, setCancellingPendingMemberId] = useState<string | null>(null);
  
  // Clear tracked member IDs when mutations complete (success or error)
  useEffect(() => {
    if (!resendInvitation.isPending && pendingResendMemberId) {
      setPendingResendMemberId(null);
    }
  }, [resendInvitation.isPending, pendingResendMemberId]);

  useEffect(() => {
    if (!archiveMember.isPending && archivingMemberId) {
      setArchivingMemberId(null);
    }
  }, [archiveMember.isPending, archivingMemberId]);

  useEffect(() => {
    if (!reactivateMember.isPending && reactivatingMemberId) {
      setReactivatingMemberId(null);
    }
  }, [reactivateMember.isPending, reactivatingMemberId]);

  // Note: No useEffect cleanup for deletingArchivedMemberId or cancellingPendingMemberId
  // because these control confirmation dialogs. Cleanup is handled by:
  // - AlertDialog's onOpenChange (user dismisses)
  // - Confirm button onClick (user confirms, resets state)

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      email: '',
      roleId: '',
    },
  });

  // Check if user has team management access via permissions
  if (!user || !profile || !hasTeamAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = (values: InviteFormValues) => {
    const inviteData: InviteUserInput = {
      email: values.email,
      roleId: values.roleId,
    };

    inviteUser.mutate(inviteData, {
      onSuccess: () => {
        form.reset({ email: '', roleId: defaultRoleId });
        setDialogOpen(false);
      }
    });
  };
  
  // Helper to get role icon based on slug
  const getRoleIconBySlug = (slug: string) => {
    switch (slug) {
      case 'admin':
        return <Shield className="h-4 w-4 text-red-500" />;
      case 'manager':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'supervisor':
        return <Crown className="h-4 w-4 text-purple-500" />;
      case 'staff':
        return <Mail className="h-4 w-4 text-green-500" />;
      default:
        return <UserCheck className="h-4 w-4 text-muted-foreground" />;
    }
  };


  // Helper function to identify pending users
  const isPendingUser = (member: any): boolean => {
    return !member.first_name || !member.last_name;
  };

  // Helper functions to filter team members by status
  const activeMembers = teamMembers.filter(member => member.status === 'active' || member.status === null);
  const archivedMembers = teamMembers.filter(member => member.status === 'archived');

  const handleResendInvitation = (member: any) => {
    setPendingResendMemberId(member.id);
    resendInvitation.mutate({ userId: member.id });
  };

  const handleArchiveMember = (member: any) => {
    setArchivingMemberId(member.id);
    archiveMember.mutate({ memberId: member.id });
  };

  const handleReactivateMember = (member: any) => {
    setReactivatingMemberId(member.id);
    reactivateMember.mutate({ memberId: member.id });
  };

  const handleDeleteMember = (member: any) => {
    setDeletingArchivedMemberId(member.id);
    deleteMember.mutate({ memberId: member.id });
  };

  const handleCancelPendingInvitation = (member: any) => {
    setCancellingPendingMemberId(member.id);
    cancelPendingInvitation.mutate({ memberId: member.id });
  };

  const handleViewProfile = (member: any) => {
    setSelectedMemberForView(member);
    setViewProfileOpen(true);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return <Shield className="h-4 w-4 text-red-500" />;
      case 'MANAGER':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'SUPERVISOR':
        return <Crown className="h-4 w-4 text-purple-500" />;
      case 'STAFF':
        return <Mail className="h-4 w-4 text-green-500" />;
      default:
        return <Mail className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'MANAGER':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'SUPERVISOR':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'STAFF':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('team.title', 'Team Management')}</h1>
          <p className="text-muted-foreground">
            {t('team.description', 'Manage your team members and invite new ones to join your company')}
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="mr-2 h-4 w-4" />
              {t('team.inviteButton', 'Invite Member')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('team.inviteDialog.title', 'Invite Team Member')}</DialogTitle>
              <DialogDescription>
                {t('team.inviteDialog.description', 'Send an invitation to a new team member to join your company')}
              </DialogDescription>
            </DialogHeader>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('team.inviteDialog.email', 'Email Address')}</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder={t('team.inviteDialog.emailPlaceholder', 'Enter email address')}
                          type="email"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="roleId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('team.inviteDialog.role', 'Role')}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('team.inviteDialog.rolePlaceholder', 'Select a role')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {rolesLoading ? (
                            <div className="flex items-center justify-center py-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                          ) : (
                            roles.map((role) => (
                              <SelectItem key={role.id} value={role.id}>
                                <div className="flex items-center space-x-2">
                                  {getRoleIconBySlug(role.slug)}
                                  <span>{role.name}</span>
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:space-x-2 sm:gap-0 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={inviteUser.isPending}
                  >
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button type="submit" disabled={inviteUser.isPending}>
                    {inviteUser.isPending ? t('team.inviteDialog.sending', 'Sending...') : t('team.inviteDialog.send', 'Send Invitation')}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Members Tabs */}
      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active" className="flex items-center space-x-2">
            <UserCheck className="h-4 w-4" />
            <span>Active ({activeMembers.length})</span>
          </TabsTrigger>
          <TabsTrigger value="archived" className="flex items-center space-x-2">
            <Archive className="h-4 w-4" />
            <span>Archived ({archivedMembers.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <div className="border rounded-lg">
            {isMobile ? (
              <div className="p-4">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('common.loading', 'Loading...')}
                  </div>
                ) : activeMembers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('team.table.noActiveMembers', 'No active team members found')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeMembers.map((member) => (
                      <TeamMemberCard
                        key={member.id}
                        member={member}
                        onResendInvitation={handleResendInvitation}
                        resendingInvitation={pendingResendMemberId}
                        onArchiveMember={canManageEmployees ? handleArchiveMember : undefined}
                        archivingMember={archivingMemberId}
                        onCancelPendingInvitation={canManageEmployees ? handleCancelPendingInvitation : undefined}
                        cancellingPendingInvitation={cancellingPendingMemberId}
                        onViewProfile={handleViewProfile}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('team.table.member', 'Team Member')}</TableHead>
                    <TableHead>{t('team.table.email', 'Email')}</TableHead>
                    <TableHead>{t('team.table.role', 'Role')}</TableHead>
                    <TableHead>{t('team.table.joinedDate', 'Joined Date')}</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {t('common.loading', 'Loading...')}
                      </TableCell>
                    </TableRow>
                  ) : activeMembers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {t('team.table.noActiveMembers', 'No active team members found')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage 
                                src={member.avatar_url} 
                                alt={generateDisplayName(member.first_name, member.last_name)}
                              />
                              <AvatarFallback>
                                {generateInitials(member.first_name, member.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="font-medium">
                              {member.first_name && member.last_name 
                                ? `${member.first_name} ${member.last_name}`
                                : t('team.table.pendingName', 'Pending')
                              }
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {getRoleIcon(member.role)}
                            <span 
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(member.role)}`}
                            >
                              {member.role}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(member.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {isPendingUser(member) && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleResendInvitation(member)}
                                  disabled={pendingResendMemberId === member.id || cancelPendingInvitation.isPending}
                                  className="text-xs"
                                >
                                  {pendingResendMemberId === member.id ? (
                                    <>
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <RotateCcw className="h-3 w-3 mr-1" />
                                      Resend
                                    </>
                                  )}
                                </Button>
                                {canManageEmployees && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-xs"
                                        disabled={cancelPendingInvitation.isPending}
                                      >
                                        <MoreHorizontal className="h-3 w-3" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem 
                                        onSelect={(e) => {
                                          e.preventDefault();
                                          setCancellingPendingMemberId(member.id);
                                        }}
                                        className="text-destructive focus:text-destructive"
                                      >
                                        <XCircle className="h-3 w-3 mr-2" />
                                        Cancel Invitation
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                                {/* Cancel Invitation Confirmation Dialog */}
                                <AlertDialog 
                                  open={cancellingPendingMemberId === member.id} 
                                  onOpenChange={(open) => !open && setCancellingPendingMemberId(null)}
                                >
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="flex items-center gap-2">
                                        <AlertTriangle className="h-5 w-5 text-destructive" />
                                        Cancel Pending Invitation
                                      </AlertDialogTitle>
                                      <AlertDialogDescription className="space-y-2">
                                        <p>
                                          Are you sure you want to cancel the invitation for <strong>{member.email}</strong>?
                                        </p>
                                        <p className="text-destructive font-medium">
                                          This will permanently remove this pending user. They will need to be invited again to join.
                                        </p>
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Keep Invitation</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => {
                                          handleCancelPendingInvitation(member);
                                          setCancellingPendingMemberId(null);
                                        }}
                                        className="bg-destructive hover:bg-destructive/90"
                                      >
                                        {cancelPendingInvitation.isPending ? (
                                          <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Cancelling...
                                          </>
                                        ) : (
                                          'Cancel Invitation'
                                        )}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                            {/* Actions dropdown - only show for non-pending users */}
                            {!isPendingUser(member) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs"
                                  >
                                    <MoreHorizontal className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleViewProfile(member)}>
                                    <Eye className="h-3 w-3 mr-2" />
                                    View Profile
                                  </DropdownMenuItem>
                                  {canManageEmployees && (
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <DropdownMenuItem 
                                          onSelect={(e) => e.preventDefault()}
                                          className="text-orange-600 focus:text-orange-700"
                                        >
                                          <Archive className="h-3 w-3 mr-2" />
                                          Archive
                                        </DropdownMenuItem>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Archive Team Member</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Are you sure you want to archive {member.first_name && member.last_name ? `${member.first_name} ${member.last_name}` : member.email}? 
                                            They will be removed from active views but their historical data will be preserved.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handleArchiveMember(member)}
                                            className="bg-orange-600 hover:bg-orange-700"
                                          >
                                            Archive
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="archived">
          <div className="border rounded-lg">
            {isMobile ? (
              <div className="p-4">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('common.loading', 'Loading...')}
                  </div>
                ) : archivedMembers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('team.table.noArchivedMembers', 'No archived team members found')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {archivedMembers.map((member) => (
                      <TeamMemberCard
                        key={member.id}
                        member={member}
                        onResendInvitation={handleResendInvitation}
                        resendingInvitation={pendingResendMemberId}
                        onReactivateMember={handleReactivateMember}
                        reactivatingMember={reactivatingMemberId}
                        onDeleteMember={handleDeleteMember}
                        deletingMember={deletingArchivedMemberId}
                        onViewProfile={handleViewProfile}
                        isArchived={true}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('team.table.member', 'Team Member')}</TableHead>
                    <TableHead>{t('team.table.email', 'Email')}</TableHead>
                    <TableHead>{t('team.table.role', 'Role')}</TableHead>
                    <TableHead>{t('team.table.joinedDate', 'Joined Date')}</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {t('common.loading', 'Loading...')}
                      </TableCell>
                    </TableRow>
                  ) : archivedMembers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {t('team.table.noArchivedMembers', 'No archived team members found')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    archivedMembers.map((member) => (
                      <TableRow key={member.id} className="opacity-75">
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage 
                                src={member.avatar_url} 
                                alt={generateDisplayName(member.first_name, member.last_name)}
                              />
                              <AvatarFallback>
                                {generateInitials(member.first_name, member.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="font-medium">
                              {member.first_name && member.last_name 
                                ? `${member.first_name} ${member.last_name}`
                                : t('team.table.pendingName', 'Pending')
                              }
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {getRoleIcon(member.role)}
                            <span 
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(member.role)}`}
                            >
                              {member.role}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(member.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {/* Actions - only show for users with manage_employee_details permission */}
                          {canManageEmployees && (
                            <div className="flex items-center space-x-2">
                              {/* Reactivate button */}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={reactivateMember.isPending}
                                    className="text-xs text-green-600 hover:text-green-700"
                                  >
                                    {reactivateMember.isPending ? (
                                      <>Loading...</>
                                    ) : (
                                      <>
                                        <UserCheck className="h-3 w-3 mr-1" />
                                        Reactivate
                                      </>
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Reactivate Team Member</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to reactivate {member.first_name && member.last_name ? `${member.first_name} ${member.last_name}` : member.email}? 
                                      They will be restored to active status and appear in team views again.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleReactivateMember(member)}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      Reactivate
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>

                              {/* More actions dropdown with Delete */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Open menu</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem 
                                    onClick={() => setDeletingArchivedMemberId(member.id)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Permanently
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>

                              {/* Controlled AlertDialog for delete confirmation - separate from dropdown */}
                              <AlertDialog 
                                open={deletingArchivedMemberId === member.id} 
                                onOpenChange={(open) => !open && setDeletingArchivedMemberId(null)}
                              >
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="text-destructive flex items-center gap-2">
                                      <AlertTriangle className="h-5 w-5" />
                                      Permanently Delete Team Member
                                    </AlertDialogTitle>
                                    <AlertDialogDescription asChild>
                                      <div className="space-y-3">
                                        <p>
                                          You are about to <strong>permanently delete</strong>{' '}
                                          <span className="font-medium">
                                            {member.first_name && member.last_name 
                                              ? `${member.first_name} ${member.last_name}` 
                                              : member.email}
                                          </span>.
                                        </p>
                                        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
                                          <p className="text-destructive font-medium text-sm mb-2">
                                            This action is IRREVERSIBLE and will:
                                          </p>
                                          <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                                            <li>Remove their account and login access permanently</li>
                                            <li>Delete their leave balances and policy assignments</li>
                                            <li>Anonymize their historical time entries and payslips</li>
                                            <li>Remove them from all future schedules</li>
                                          </ul>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                          Historical records will be preserved with anonymized references for audit purposes.
                                        </p>
                                      </div>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => {
                                        handleDeleteMember(member);
                                        setDeletingArchivedMemberId(null);
                                      }}
                                      disabled={deleteMember.isPending}
                                      className="bg-destructive hover:bg-destructive/90"
                                    >
                                      {deleteMember.isPending ? (
                                        <>
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                          Deleting...
                                        </>
                                      ) : (
                                        'Delete Permanently'
                                      )}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* View Profile Dialog */}
      {selectedMemberForView && (
        <ViewProfileDialog
          open={viewProfileOpen}
          onOpenChange={setViewProfileOpen}
          teamMember={selectedMemberForView}
        />
      )}
    </div>
  );
};

export default TeamManagement;
